import { ErrorHandler, Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  forgotPasswordRoutePath,
  getLoginChildRoutePath,
  getLoginRoutePath,
  getNonUserRoutePath,
  resetPasswordRoutePath,
} from '../../app.routes';
import { AuthService } from '../auth/auth.service';
import { LoggingService } from '../logging/logging.service';
import { AlertService } from '../alert/alert.service';
import { AppService } from '../capacitor/app.service';
import { CapacitorService } from '../capacitor/capacitor.service';
import { PlatformService } from '../capacitor/platform.service';

@Injectable({
  providedIn: 'root',
})
export class UniversalLinkService {
  private readonly appService = inject(AppService);
  private readonly authService = inject(AuthService);
  private readonly alertService = inject(AlertService);
  private readonly capacitorService = inject(CapacitorService);
  private readonly logger = inject(LoggingService);
  private readonly errorHandler = inject(ErrorHandler);
  private readonly platformService = inject(PlatformService);
  private readonly router = inject(Router);

  private launchUrl?: Promise<boolean>;
  private launchUrlSettled = false;

  /**
   * Routes the deep link the app was launched with, if any.
   *
   * Resolves true once that link owns the app's navigation; every caller that routes on start up
   * must await this and skip its own navigation when it resolves true, otherwise the default
   * start up route races the deep link and the link's query params (an invite code, a redirect)
   * are lost. The launch URL is consumed at most once per app start no matter how many callers
   * await it.
   *
   * Only callers that ask while the read is still in flight - the start up window - can be told
   * to stand down. `AuthRedirectComponent` owns the `''` route and re-runs every time `/` is
   * activated, so once the read has settled this resolves false and those later activations route
   * normally rather than deferring to a link that has already been consumed.
   */
  handleLaunchUrl(): Promise<boolean> {
    if (this.launchUrlSettled) {
      return Promise.resolve(false);
    }

    this.launchUrl ??= this.routeLaunchUrl().finally(() => {
      this.launchUrlSettled = true;
    });

    return this.launchUrl;
  }

  async handleUniversalLink(url: string): Promise<boolean | void> {
    try {
      this.logger.log('Handling universal link:', url);

      const parsedUrl = new URL(url);
      // Non special schemes (olympiafitnessri://) parse an absent path as '', which is not routable
      const pathname = parsedUrl.pathname || '/';
      const searchParams = parsedUrl.searchParams;
      const route = pathname + parsedUrl.search;
      const redirect = searchParams.get('redirect');
      const redirectTarget = redirect || route;
      const authUser = await this.authService.getAuthUser();

      // Check for Supabase auth fragments in the URL hash
      // TODO BG can we combine this with the auth guard?
      if (parsedUrl.hash) {
        const hashParams = new URLSearchParams(parsedUrl.hash.substring(1));
        const error = hashParams.get('error');
        const errorCode = hashParams.get('error_code');
        const errorDescription = hashParams.get('error_description');

        // Handle error conditions first
        if (error) {
          let message = errorDescription || 'Unknown error';

          // Provide more user-friendly error messages for specific cases
          if (errorCode === 'otp_expired') {
            message =
              'Your password reset link has expired. Please request a new one.';
          }

          await this.alertService.createNoticeWithOptions({
            message,
            duration: 5000,
            position: 'bottom',
          });

          // Redirect to appropriate page based on error type
          if (errorCode === 'otp_expired') {
            return this.router.navigate([
              getLoginChildRoutePath(forgotPasswordRoutePath),
            ]);
          } else {
            return this.router.navigate([getLoginRoutePath()]);
          }
        }

        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const type = hashParams.get('type');

        // Handle password recovery specifically
        if (accessToken && refreshToken && type === 'recovery') {
          try {
            const { error } = await this.authService.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });

            if (error) {
              throw error;
            }

            // Password recovery - redirect to reset password page
            return this.router.navigate([
              getNonUserRoutePath(resetPasswordRoutePath),
            ]);
          } catch (error) {
            console.error('Failed to set auth session from deep link:', error);
            // Fall through to normal routing if auth fails
          }
        }
      }

      if (!authUser) {
        this.logger.log(
          `No auth user, redirecting to login: ${redirectTarget}`,
        );
        return this.router.navigateByUrl(
          `${getLoginRoutePath()}?redirect=${encodeURIComponent(redirectTarget)}`,
        );
      }

      if (!redirect) {
        this.logger.log(`No redirect, redirecting to route: ${route}`);
        return this.router.navigateByUrl(route);
      }

      this.logger.log(
        `User found, initializing app for user: ${authUser.id}, redirect: ${redirect}`,
      );
      await this.authService.initializeAppForUser(authUser.id, redirect);
    } catch (error) {
      this.logger.error('Error handling universal link:', error);
      this.errorHandler.handleError(error);
      return false;
    }
  }

  private async routeLaunchUrl(): Promise<boolean> {
    if (!this.capacitorService.mobile) {
      return false;
    }

    try {
      await this.platformService.ready();

      const url = (await this.appService.getLaunchUrl())?.url;

      if (!url || !this.isDeepLink(url)) {
        return false;
      }

      this.logger.log('Launch URL detected:', url);
      // handleUniversalLink resolves void or the router's own result on the paths that route, and
      // only an explicit false when it could not, so a failed handle leaves start up routing free
      // to fall back rather than stranding the caller on a link that went nowhere
      return (await this.handleUniversalLink(url)) !== false;
    } catch (error) {
      this.logger.error('Error handling launch URL:', error);
      return false;
    }
  }

  /**
   * A bare origin is how the platform reports an ordinary app launch, so it carries no
   * destination and must not pre-empt start up routing.
   */
  private isDeepLink(url: string): boolean {
    const parsedUrl = new URL(url);
    const pathname = parsedUrl.pathname || '/';
    return pathname !== '/' || !!parsedUrl.search || !!parsedUrl.hash;
  }
}
